import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Loader2, MessageCircle, Send, CornerDownLeft, User, UserCheck, Trash2, ChevronDown, Edit2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { he } from "date-fns/locale";
import { Link, useLocation } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface QnASectionProps {
  systemId: string;
  ownerId: string;
}

// Regex protections
const LINK_REGEX = /(https?:\/\/|www\.|[a-zA-Z0-9-]+\.(com|co\.il|org|net|io|gov|edu))/i;
const TOXIC_REGEX = /(palestine|free palestine|balestin|פלסטין|בלסטין|זונה|שרמוטה|בן זונה|מניאק|נאצי|היטלר|fuck|shit|bitch|whore)/i;

export const QnASection = ({ systemId, ownerId }: QnASectionProps) => {
  const [question, setQuestion] = useState("");
  
  // State for answers
  const [answerInput, setAnswerInput] = useState<{ [key: string]: string }>({});
  const [answeringId, setAnsweringId] = useState<string | null>(null);
  
  // State for editing questions
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [editQuestionText, setEditQuestionText] = useState("");

  // Pagination
  const [visibleCount, setVisibleCount] = useState(3);
  
  const queryClient = useQueryClient();
  const [session, setSession] = useState<any>(null);
  const location = useLocation();

  // Check auth & role
  const { data: profile } = useQuery({
    queryKey: ['my-profile-role', session?.user?.id],
    queryFn: async (): Promise<any> => {
      if (!session?.user?.id) return null;
      const { data } = await supabase.from('profiles').select('role').eq('id', session.user.id).single();
      return data;
    },
    enabled: !!session?.user?.id
  });

  useState(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
  });

  const isOwner = session?.user?.id === ownerId;
  const isAdmin = (profile as any)?.role === 'admin';

  // Fetch Questions
  const { data: questions, isLoading } = useQuery({
    queryKey: ['questions', systemId],
    queryFn: async (): Promise<any[]> => {
      // Using the computed column 'user_profile' to join with the view
      const { data, error } = await supabase
        .from('questions' as any)
        .select('*, user:user_profile(display_name)')
        .eq('system_id', systemId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    }
  });

  // Ask Question Mutation
  const askMutation = useMutation({
    mutationFn: async (text: string) => {
      if (!session) throw new Error("Must be logged in");
      
      // Client-side checks
      if (LINK_REGEX.test(text)) throw new Error("חל איסור לפרסם קישורים.");
      if (TOXIC_REGEX.test(text)) throw new Error("התוכן מכיל שפה אסורה.");

      const { error } = await supabase
        .from('questions' as any)
        .insert({
          system_id: systemId,
          user_id: session.user.id,
          content: text
        } as any);
        
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("השאלה נשלחה למפרסם");
      setQuestion("");
      queryClient.invalidateQueries({ queryKey: ['questions', systemId] });
    },
    onError: (error: any) => {
      const msg = error.message || "שגיאה בשליחת השאלה";
      if (msg.includes("קישורים")) {
        toast.error("תוכן אסור", { description: "אין לפרסם קישורים או כתובות אינטרנט." });
      } else if (msg.includes("שפה אסורה") || msg.includes("פוגענית")) {
        toast.error("תוכן לא הולם", { description: "הודעתך מכילה מילים המפרות את כללי הקהילה." });
      } else if (msg.includes("שאלה אחת לכל מערכת")) {
        toast.error("מגבלת תדירות", { description: "ניתן לשאול שאלה אחת בלבד כל 12 שעות עבור מערכת זו." });
      } else {
        toast.error(msg);
      }
    }
  });

  // Edit Question Mutation
  const editQuestionMutation = useMutation({
    mutationFn: async ({ qId, text }: { qId: string, text: string }) => {
      // Also validate on edit
      if (LINK_REGEX.test(text)) throw new Error("חל איסור לפרסם קישורים.");
      if (TOXIC_REGEX.test(text)) throw new Error("התוכן מכיל שפה אסורה.");

      const { error } = await (supabase.from('questions') as any)
        .update({ content: text })
        .eq('id', qId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("השאלה עודכנה");
      setEditingQuestionId(null);
      queryClient.invalidateQueries({ queryKey: ['questions', systemId] });
    },
    onError: (error: any) => {
      const msg = error.message || "שגיאה בעדכון השאלה";
      if (msg.includes("קישורים")) {
        toast.error("תוכן אסור", { description: "אין לפרסם קישורים." });
      } else if (msg.includes("שפה אסורה") || msg.includes("פוגענית")) {
        toast.error("תוכן לא הולם", { description: "הודעתך מכילה מילים המפרות את כללי הקהילה." });
      } else {
        toast.error(msg);
      }
    }
  });

  // Answer Mutation (Create or Update)
  const answerMutation = useMutation({
    mutationFn: async ({ qId, text }: { qId: string, text: string }) => {
      const { error } = await (supabase.from('questions') as any)
        .update({ 
          answer: text,
          answered_at: new Date().toISOString()
        })
        .eq('id', qId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("התשובה פורסמה");
      setAnsweringId(null);
      queryClient.invalidateQueries({ queryKey: ['questions', systemId] });
    },
    onError: () => toast.error("שגיאה בשמירת התשובה")
  });

  // Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: async (qId: string) => {
      const { error } = await supabase
        .from('questions' as any)
        .delete()
        .eq('id', qId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("השאלה נמחקה");
      queryClient.invalidateQueries({ queryKey: ['questions', systemId] });
    },
    onError: () => toast.error("שגיאה במחיקת השאלה")
  });

  const handleAsk = (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim()) return;
    
    if (LINK_REGEX.test(question)) {
      toast.error("תוכן אסור", { description: "אין לפרסם קישורים או כתובות אינטרנט." });
      return;
    }
    if (TOXIC_REGEX.test(question)) {
      toast.error("תוכן לא הולם", { description: "הודעתך מכילה מילים המפרות את כללי הקהילה." });
      return;
    }

    askMutation.mutate(question);
  };

  const handleAnswer = (qId: string) => {
    const text = answerInput[qId];
    if (!text?.trim()) return;
    answerMutation.mutate({ qId, text });
  };

  const handleEditQuestionSave = (qId: string) => {
    if (!editQuestionText.trim()) return;
    
    if (LINK_REGEX.test(editQuestionText)) {
      toast.error("תוכן אסור", { description: "אין לפרסם קישורים." });
      return;
    }
    if (TOXIC_REGEX.test(editQuestionText)) {
      toast.error("תוכן לא הולם", { description: "הודעתך מכילה מילים המפרות את כללי הקהילה." });
      return;
    }

    editQuestionMutation.mutate({ qId, text: editQuestionText });
  };

  const startEditingQuestion = (qId: string, currentContent: string) => {
    setEditingQuestionId(qId);
    setEditQuestionText(currentContent);
  };

  if (isLoading) return <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-300"/></div>;

  const displayedQuestions = questions?.slice(0, visibleCount);
  const remainingCount = (questions?.length || 0) - visibleCount;

  return (
    <div className="space-y-8" id="qna-section">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-blue-600" />
          שאלות ותשובות
        </h3>
        <span className="text-sm text-slate-500 bg-slate-50 px-2 py-1 rounded-md">
          {questions?.length || 0} שאלות
        </span>
      </div>

      {/* Ask Box */}
      <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
        {!session ? (
          <div className="text-center py-4 space-y-3">
            <p className="text-slate-600 font-medium">יש לך שאלה למפרסם?</p>
            <Button variant="outline" asChild className="bg-white hover:bg-slate-50">
              <Link to="/login" state={{ from: location }}>התחבר כדי לשאול שאלה</Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={handleAsk} className="space-y-3">
            <div className="flex gap-3">
              <Avatar className="w-8 h-8 md:w-10 md:h-10 border border-slate-200">
                <AvatarFallback className="bg-white text-slate-500 text-xs">אני</AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-2">
                <Textarea 
                  placeholder="מה תרצה לדעת על המערכת?" 
                  className="min-h-[80px] bg-white border-slate-200 resize-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  maxLength={500}
                />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-400">{question.length}/500</span>
                  <Button 
                    size="sm" 
                    type="submit" 
                    disabled={askMutation.isPending || !question.trim()}
                    className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                  >
                    {askMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
                    שלח שאלה
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Questions List */}
      <div className="space-y-6">
        {questions?.length === 0 ? (
          <p className="text-center text-slate-400 text-sm py-4">אין עדיין שאלות. היה הראשון לשאול!</p>
        ) : (
          <>
            {displayedQuestions?.map((q: any) => {
              const isAuthor = session?.user?.id === q.user_id;
              const canDelete = isAdmin || isAuthor;
              const canEditQuestion = isAuthor;
              const canReplyOrEditAnswer = isAdmin || isOwner;

              return (
                <div key={q.id} className="group relative">
                  
                  {/* Question */}
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center gap-1 shrink-0">
                      <Avatar className="w-8 h-8 border border-slate-100">
                        <AvatarFallback className="bg-slate-100 text-slate-500 text-xs">
                          <User className="w-4 h-4" />
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-baseline justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{q.user?.display_name || 'משתמש'}</span>
                          {isAdmin && <span title="Admin View"><ShieldAlert className="w-3 h-3 text-red-500" /></span>}

                          {/* Action Buttons */}
                          <div className="flex items-center gap-1 mr-2">
                            {canEditQuestion && !q.answer && !editingQuestionId && (
                              <button 
                                onClick={() => startEditingQuestion(q.id, q.content)}
                                className="p-1 text-slate-400 hover:text-blue-600 rounded transition-colors"
                                title="ערוך שאלה"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {canDelete && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors" title="מחק שאלה">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="rounded-[28px] max-w-[320px] p-6 bg-white/95 backdrop-blur-sm border-none shadow-xl gap-6" dir="rtl">
                                  <div className="flex flex-col items-center text-center gap-2">
                                    <span className="text-lg font-semibold text-slate-900 tracking-tight">מחיקת תוכן</span>
                                    <span className="text-[15px] text-slate-500 leading-relaxed font-normal">
                                      האם אתה בטוח שברצונך למחוק את השאלה? הפעולה אינה ניתנת לשחזור.
                                    </span>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-3 w-full">
                                    <AlertDialogCancel className="w-full h-11 rounded-full bg-slate-100 text-slate-900 font-medium hover:bg-slate-200 border-none mt-0 shadow-none text-[15px]">
                                      ביטול
                                    </AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => deleteMutation.mutate(q.id)} 
                                      className="w-full h-11 rounded-full bg-transparent hover:bg-red-50 text-red-500 font-medium shadow-none border-none text-[15px]"
                                    >
                                      מחק
                                    </AlertDialogAction>
                                  </div>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-slate-400">
                          {formatDistanceToNow(new Date(q.created_at), { addSuffix: true, locale: he })}
                        </span>
                      </div>
                      
                      {editingQuestionId === q.id ? (
                        <div className="space-y-2">
                          <Textarea 
                            value={editQuestionText}
                            onChange={(e) => setEditQuestionText(e.target.value)}
                            className="bg-white"
                          />
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => setEditingQuestionId(null)}>ביטול</Button>
                            <Button size="sm" onClick={() => handleEditQuestionSave(q.id)} disabled={editQuestionMutation.isPending}>שמור</Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-slate-700 text-sm leading-relaxed bg-white p-3 rounded-tr-none rounded-xl border border-slate-100 inline-block shadow-sm">
                          {q.content}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Answer */}
                  {q.answer && answeringId !== q.id ? (
                    <div className="flex gap-4 mt-3 mr-8 md:mr-12 group/answer relative">
                      <div className="flex-1 space-y-1 text-left" dir="rtl">
                        <div className="flex items-baseline justify-between">
                          <span className="text-sm font-bold text-blue-700 flex items-center gap-1.5">
                            <UserCheck className="w-3.5 h-3.5" />
                            תשובת המפרסם
                          </span>
                          <div className="flex items-center gap-2">
                            {canReplyOrEditAnswer && (
                              <button 
                                onClick={() => {
                                  setAnsweringId(q.id);
                                  setAnswerInput({ ...answerInput, [q.id]: q.answer });
                                }}
                                className="text-xs text-slate-400 hover:text-blue-600 opacity-0 group-hover/answer:opacity-100 transition-opacity"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                            )}
                            {q.answered_at && (
                              <span className="text-xs text-slate-400">
                                {formatDistanceToNow(new Date(q.answered_at), { addSuffix: true, locale: he })}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="bg-blue-50/50 p-3 rounded-tr-none rounded-xl border border-blue-100 text-slate-700 text-sm leading-relaxed">
                          {q.answer}
                        </div>
                      </div>
                    </div>
                  ) : canReplyOrEditAnswer ? (
                    // Owner Answer Input (New or Edit)
                    <div className="mt-3 mr-8 md:mr-12">
                      {answeringId === q.id ? (
                        <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
                          <div className="flex items-center gap-2 mb-1">
                            <CornerDownLeft className="w-3 h-3 text-blue-600" />
                            <span className="text-xs font-bold text-blue-600">תשובתך:</span>
                          </div>
                          <Textarea 
                            placeholder="כתוב את התשובה שלך כאן..."
                            className="min-h-[80px] bg-white border-blue-200 focus:border-blue-500"
                            value={answerInput[q.id] || ""}
                            onChange={(e) => setAnswerInput({...answerInput, [q.id]: e.target.value})}
                          />
                          <div className="flex gap-2 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => setAnsweringId(null)}>ביטול</Button>
                            <Button 
                              size="sm" 
                              onClick={() => handleAnswer(q.id)}
                              disabled={answerMutation.isPending}
                              className="bg-blue-600 text-white hover:bg-blue-700"
                            >
                              פרסם תשובה
                            </Button>
                          </div>
                        </div>
                      ) : !q.answer && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setAnsweringId(q.id)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 px-2 text-xs font-medium"
                        >
                          <CornerDownLeft className="w-3 h-3 ml-1" />
                          ענה לשאלה
                        </Button>
                      )}
                    </div>
                  ) : (
                    !q.answer && (
                      <div className="mt-2 mr-12 text-xs text-slate-400 italic">
                        טרם התקבלה תשובה
                      </div>
                    )
                  )}
                </div>
              );
            })}

            {remainingCount > 0 && (
              <div className="text-center pt-4">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setVisibleCount(prev => prev + 3)}
                  className="text-slate-500 hover:text-slate-900"
                >
                  <ChevronDown className="w-4 h-4 ml-2" />
                  הצג עוד {remainingCount > 3 ? 3 : remainingCount} שאלות
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};